import { useEffect } from 'react'

/**
 * The frame a live stream is shown in, shared by both provider stages
 * (LiveKitStreamStage / VideosdkStreamStage) so a viewer cannot tell which SFU
 * is behind the picture.
 */

export function StageSurface({ children }) {
  return (
    <div
      className="relative aspect-video w-full overflow-hidden rounded-3xl"
      style={{ background: 'linear-gradient(135deg,#2A1F14,#120F0C)', border: '1px solid rgba(200,146,42,.18)' }}
    >
      {children}
    </div>
  )
}

/** What fills the stage when there is no picture yet - connecting, camera off,
 *  waiting for the astrologer. Always says what to expect next, because an
 *  empty black rectangle reads as "broken". */
export function StageNotice({ title, body }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
      <p className="font-cinzel text-lg text-white sm:text-xl">{title}</p>
      {body && <p className="mt-2 max-w-sm text-xs font-light leading-relaxed text-white/50">{body}</p>}
    </div>
  )
}

export function ControlButton({ active, onClick, onIcon: OnIcon, offIcon: OffIcon, label }) {
  const Icon = active ? OnIcon : OffIcon
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[11px] font-bold uppercase tracking-[.15em] transition-all duration-300"
      style={
        active
          ? { background: '#1C140A', color: '#FFF' }
          : { background: 'rgba(200,146,42,.08)', color: '#8B7355', border: '1px solid rgba(200,146,42,.25)' }
      }
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  )
}

/**
 * Attach a raw MediaStreamTrack to a <video>/<audio> element.
 *
 * Only the VideoSDK stage needs this - LiveKit ships a <VideoTrack> component
 * that does it - but it lives here next to the rest of the stage plumbing.
 * Clears srcObject on teardown so a stopped track is never left attached.
 */
export function useMediaTrack(ref, track, enabled) {
  useEffect(() => {
    const el = ref.current
    if (!el) return undefined
    if (enabled && track) {
      const stream = new MediaStream()
      stream.addTrack(track)
      el.srcObject = stream
      el.play?.().catch(() => {})
    } else {
      el.srcObject = null
    }
    return () => {
      el.srcObject = null
    }
  }, [ref, track, enabled])
}
